import { createFileRoute, Navigate, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useAcceptInviteCode } from '@/organization/invite-codes-api'
import { AxiosError } from 'axios'
import { DialogAutoClose, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { useEmailManagement } from '@knowsuchagency/django-allauth'
import { UserProfileDialog } from '@/components/UserProfileDialog'
import { AlertCircle } from 'lucide-react'
import { useOrganizationIdWithFallback } from "@/organization/api"

export const Route = createFileRoute('/_authenticated/organization/no-organizations')({
  component: RouteComponent,
})

function RouteComponent() {
  const organizationId = useOrganizationIdWithFallback()
  const [inviteCode, setInviteCode] = useState('')
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)
  const [successData, setSuccessData] = useState<{ organizationId: number; organizationName: string } | null>(null)
  const [showProfileDialog, setShowProfileDialog] = useState(false)
  const navigate = useNavigate()
  const acceptInviteCode = useAcceptInviteCode()
  const { emails } = useEmailManagement()

  if (organizationId) {
    return <Navigate to="/organization/$organizationId" params={{ organizationId }} />
  }

  // Check if primary email is unverified
  const primaryEmail = (emails || []).find(email => email.primary)
  const isPrimaryEmailUnverified = primaryEmail && !primaryEmail.verified

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (inviteCode.trim()) {
      acceptInviteCode.mutate(inviteCode.trim(), {
        onSuccess: (data) => {
          // Show success dialog first
          setSuccessData({
            organizationId: data.organization.id,
            organizationName: data.organization.name,
          })
          setShowSuccessDialog(true)
        },
      })
    }
  }

  const handleDialogClose = (open: boolean) => {
    setShowSuccessDialog(open)
    if (!open && successData) {
      // Navigate to the organization page after dialog closes
      navigate({
        to: '/organization/$organizationId',
        params: { organizationId: successData.organizationId },
      })
    }
  }

  // Get error message from response
  const errorMessage = acceptInviteCode.isError && acceptInviteCode.error instanceof AxiosError
    ? acceptInviteCode.error.response?.data?.error || 'An error occurred. Please try again.'
    : 'An error occurred. Please try again.'

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="text-center space-y-6 max-w-md w-full">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold">No Organizations Available</h1>
          <p className="text-muted-foreground">
            You're not part of any organizations yet.
          </p>
        </div>

        {isPrimaryEmailUnverified && (
          <div className="p-4 rounded-md bg-yellow-50 border border-yellow-200 text-left">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div>
                  <p className="font-semibold text-yellow-900">Verify Your Email</p>
                  <p className="text-sm text-yellow-800">
                    Verify your email address ({primaryEmail?.email}) and you may be automatically added to your organization.
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => setShowProfileDialog(true)}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white"
                >
                  Open Email preferences
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            If you have an invite code, you can use it to join an organization.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Input
              type="text"
              placeholder="Enter invite code (e.g., happy shiny turtle)..."
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              className="w-full"
            />
          </div>
          <Button
            type="submit"
            variant="outline"
            className="w-full"
            disabled={acceptInviteCode.isPending || !inviteCode.trim()}
          >
            {acceptInviteCode.isPending ? 'Joining...' : 'Join Organization'}
          </Button>
        </form>

        {acceptInviteCode.isError && (
          <div className="p-4 rounded-md bg-red-50 border border-red-200 text-left">
            <p className="font-semibold text-red-800">Error</p>
            <p className="text-sm text-red-700">
              {errorMessage}
            </p>
          </div>
        )}

        <DialogAutoClose
          open={showSuccessDialog}
          onOpenChange={handleDialogClose}
          duration={5000}
        >
          <DialogHeader>
            <DialogTitle>Successfully Joined!</DialogTitle>
            <DialogDescription>
              You've successfully joined {successData?.organizationName}. Redirecting you now...
            </DialogDescription>
          </DialogHeader>
        </DialogAutoClose>

        <UserProfileDialog
          isOpen={showProfileDialog}
          onOpenChange={setShowProfileDialog}
        />
      </div>
    </div>
  );
}
